import type { CommonSettingsType } from "../../common-deprecated/settings/fetchCommonSettings";
import type { TradeInAemSettingsType } from "../redux/reducers/TradeInAemSettingsReducer";
import {
    type GetLabelType,
    type UseLabelType,
    type UseOptionalLabelType,
    getLabelFn,
    getUseLabel,
    getUseOptionalLabel,
} from "../../common-deprecated/hooks/useLabel";
import { type LabelComponentType, getLabelComponent } from "../../common-deprecated/components/label/Label";
import { TradeInAemModelSelectType, TradeInAemNavStep } from "./constants";
import type { TradeInAemExtraVehicleDetailsPdfAllPossibleKeysType } from "./types";

export const tradeInAemLabels = {
    // ----------------------------------------------------------------------
    // General
    // ----------------------------------------------------------------------
    tradeInAemConfirm: "Confirm",
    tradeInAemBack: "Back",
    tradeInAemLoading: "Loading",
    tradeInAemFieldRequired: "This field is required",

    // Result block
    tradeInAemMileageFormat: "{value} km",
    tradeInAemVehicleResultBlockTitle: "Your car",

    // Navigation
    tradeInAemStepFindVehicle: "Vehicle identification",
    tradeInAemStepVehicleDetails: "Vehicle state",
    tradeInAemStepVehicleValue: "Your valuations",
    tradeInAemStepModelSelect: "Your new car",
    tradeInAemStepSelectDealer: "Select dealer",
    tradeInAemStepContactForm: "Personal details",
    tradeInAemStepValuation: "Your valuations",
    tradeInAemNavigationTitle: "Trade in valuation",

    // Valuation
    tradeInAemValuationTitle: "Your valuation",
    tradeInAemValuationLoadingHeaderTitle: "Hold on",
    tradeInAemValuationLoadingHeaderDescription: "We are preparing your estimated valuation for you",
    tradeInAemValuationFailedHeaderTitle: "Oh no..",
    tradeInAemValuationFailedHeaderDescription: "Sorry we are not able to provide an online valuation",
    tradeInAemValuationFailedContent:
        "Please contact your nearest Toyota dealer who will be able to assist with your part exchange valuation.",
    tradeInAemValuationFailedEditCarDetails: "Edit my car details",
    // ----------------------------------------------------------------------
    // Find Vehicle
    // ----------------------------------------------------------------------

    // General
    tradeInAemFindVehicleOr: "Or",
    tradeInAemFindVehiclePreviouslySearchedVehicles: "Previously searched cars",
    tradeInAemFindVehicleCannotFindMyCar: "Can't find my car",
    tradeInAemFindVehicleUseOptionsToFindYourCar: "Use the options below to find your car",

    // Header
    tradeInAemFindVehicleHeaderTitle: "Tell us about your car",
    tradeInAemFindVehicleHeaderDescription:
        "Identify your current car based on license plate or VIN number. Don’t know any of these by heart, then fill in the questionnaire.",

    // License Plate
    tradeInAemFindVehicleLicensePlateTitle: "Find your license plate",
    tradeInAemFindVehicleLicensePlatePlaceHolder: "Search",
    tradeInAemFindVehicleLicensePlateDescription: "Format 1-ABC-123",
    tradeInAemFindVehicleLicensePlateError:
        "Sorry, we are unable to find this registration. Please try again or enter your vehicle details manually.",

    // VIN number
    tradeInAemFindVehicleVinNumberTitle: "Find your VIN number",
    tradeInAemFindVehicleVinNumberPlaceHolder: "Search",
    tradeInAemFindVehicleVinNumberDescription: "Format 1-ABC-123",
    tradeInAemFindVehicleVinNumberError:
        "Sorry, we are unable to find this registration. Please try again or enter your vehicle details manually.",

    // ----------------------------------------------------------------------
    // Resolve
    // ----------------------------------------------------------------------

    // General
    tradeInAemResolveFailed: "No vehicle found, please update your configuration.",

    tradeInAemResolveSelect: "Select...",
    tradeInAemResolveModel: "Model",
    tradeInAemResolveBody: "Body",
    tradeInAemResolveGrade: "Grade",

    // THEL
    tradeInAemResolveMake: "Make",
    tradeInAemResolveName: "Name",

    // TES
    tradeInAemResolveCategory: "Category",
    tradeInAemResolveManufacturer: "Manufacturer",
    tradeInAemResolveMonth: "Month",
    tradeInAemResolveYear: "Year",
    tradeInAemResolveEngine: "Engine",
    tradeInAemResolveGeneration: "Generation",
    tradeInAemResolveSeats: "Seats",
    tradeInAemResolveFuel: "Fuel",
    tradeInAemResolveTransmission: "Transmission",
    tradeInAemResolveWheeldrive: "Wheeldrive",

    // TBG
    tradeInAemResolvePlate: "Plate",
    tradeInAemResolveDoors: "Doors",
    tradeInAemResolvePower: "Power",

    // ----------------------------------------------------------------------
    // Vehicle Details
    // ----------------------------------------------------------------------

    // General
    tradeInAemVehicleDetailsResultTitle: "Your car",
    tradeInAemVehicleDetailsLicensePlate: "License plate",
    tradeInAemVehicleDetailsVin: "VIN",
    tradeInAemVehicleDetailsSelect: "Select...",
    tradeInAemVehicleDetailsFirstSelectYear: "First select a year...",

    // Header
    tradeInAemVehicleDetailsHeaderTitle: "Your car was identified",
    tradeInAemVehicleDetailsHeaderDescription:
        "Now that your vehicle is identified, please provide us some more info on the state of your car. This will help your retailer to give you a realistic offer.",

    // ----------------------------------------------------------------------
    // Vehicle value
    // ----------------------------------------------------------------------

    // Header
    tradeInAemVehicleValueHeaderTitle: "Thank you!",
    tradeInAemVehicleValueHeaderDescription:
        "Your next Toyota is only a few steps away. Please find the estimated value of your old car below. Your retailer will be in touch to make you a detailed offer. ",

    // Outstanding loan
    tradeInAemOutstandingLoanEstimation: "Our estimation",
    tradeInAemOutstandingLoanFinance: "Outstanding finance",
    tradeInAemOutstandingLoanDescription:
        "Based on the valuation of your vehicle and the outstanding finance amount, the amount you can contribute to your online purchase is:",

    // Content
    tradeInAemVehicleValuePdf: "Download pdf",
    tradeInAemVehicleValueGetQuote: "Get personalised quote",
    tradeInAemVehicleValueAddToBasket: "Add to deal",

    // Disclaimers
    tradeInAemVehicleValueDisclaimer: "",
    tradeInAemVehicleValueValidity: "Valid until {date}",

    // ----------------------------------------------------------------------
    // Model select
    // ----------------------------------------------------------------------

    // General
    tradeInAemModelSelectSkip: "Skip",
    tradeInAemModelSelectSelect: "Select",
    tradeInAemModelSelectSelected: "Selected",
    tradeInAemModelSelectNewCars: "New Cars",
    tradeInAemModelSelectStockCars: "Stock Cars",
    tradeInAemModelSelectUsedCars: "Used Cars",
    tradeInAemModelSelectNone: "None",
    tradeInAemModelSelectSelectModel: "Select model",

    // Header
    tradeInAemModelSelectHeaderTitle: "Choose your next car",
    tradeInAemModelSelectHeaderDescription: "Time for the fun part! Select your preferred new car.",

    // ----------------------------------------------------------------------
    // Select Dealer
    // ----------------------------------------------------------------------

    // Header
    tradeInAemSelectDealerHeaderTitle: "Select your dealer of choice",
    tradeInAemSelectDealerHeaderDescription:
        "Donec ullamcorper nulla non metus auctor fringilla. Aenean eu leo quam. Pellentesque ornare sem lacinia quam venenatis vestibulum.",

    // ----------------------------------------------------------------------
    // Contact form
    // ----------------------------------------------------------------------

    // Header
    tradeInAemContactFormHeaderTitle: "Enter your personal details",
    tradeInAemContactFormHeaderDescription:
        "Almost there! Enter your personal details and allow your selected retailer to send you a detailed proposal",
    tradeInAemContactFormYourDetails: "Your personal details",
    tradeInAemContactFormYourRetailer: "Your retailer",
    tradeInAemContactDealerAddress: "{line1}\n{city} {zip}, {region}", // placeholders: line1, line2, city, zip, region, province, country
    tradeInAemContactEmailError: "Please enter a valid e-mail address",
    tradeInAemContactDisclaimerAccept: "Accept",
    tradeInAemContactDisclaimerReject: "Reject",

    // ----------------------------------------------------------------------
    // Summary
    // ----------------------------------------------------------------------

    // Header
    tradeInAemSummaryHeaderTitle: "Thank you!",
    tradeInAemSummaryHeaderDescription:
        "Your next Toyota is only a few steps away. Please find the estimated value of your old car below. Your retailer will be in touch to make you a detailed offer. ",
    tradeInAemSummaryCustomerIntention: "Customer intention",
    tradeInAemSummaryBuyingIntention: "Buying intention",
    tradeInAemSummaryYourDetails: "Your details",
    tradeInAemSummaryYourRetailer: "Your retailer",
    tradeInAemSummaryPhone: "Tel {value}",
    tradeInAemDownloadPdf: "Download pdf",
    tradeInAemBackToWebsite: "Back to the website",
    tradeInAemDisclaimer: "",
    tradeInAemOfferValid: "Offer valid until {value}",

    // ----------------------------------------------------------------------
    // Extra vehicle details pdf
    // ----------------------------------------------------------------------

    // Base
    tradeInAemPdfExtraDetailsManufacturer: "Manufacturer",
    tradeInAemPdfExtraDetailsModel: "Model",
    tradeInAemPdfExtraDetailsGrade: "Grade",
    tradeInAemPdfExtraDetailsGeneration: "Generation",
    tradeInAemPdfExtraDetailsEngine: "Engine",
    tradeInAemPdfExtraDetailsPowerHp: "Power HP",
    tradeInAemPdfExtraDetailsPowerPs: "Power PS",
    tradeInAemPdfExtraDetailsPowerKw: "Power KW",
    tradeInAemPdfExtraDetailsBody: "Body",
    tradeInAemPdfExtraDetailsDoors: "Doors",
    tradeInAemPdfExtraDetailsFuel: "Fuel",
    tradeInAemPdfExtraDetailsTransmission: "Transmission",
    tradeInAemPdfExtraDetailsGears: "Gears",
    tradeInAemPdfExtraDetailsColour: "Colour",
    tradeInAemPdfExtraDetailsRegDate: "Registration Date",
    tradeInAemPdfExtraDetailsVin: "Vin",
    tradeInAemPdfExtraDetailsSeats: "Seats",
    tradeInAemPdfExtraDetailsWheeldrive: "Wheeldrive",
    tradeInAemPdfExtraDetailsCc: "Cc",
    tradeInAemPdfExtraDetailsYear: "Year",

    // TAF
    tradeInAemPdfExtraDetailsEngineSize: "Engine size",
    tradeInAemPdfExtraDetailsNumberOfGears: "Number of gears",
    tradeInAemPdfExtraDetailsPriceNetto: "Price netto",
    tradeInAemPdfExtraDetailsPriceBrutto: "Price brutto",
    tradeInAemPdfExtraDetailsTrimLine: "Trim line",
    tradeInAemPdfExtraDetailsDriveType: "Drive type",

    // TSW
    tradeInAemPdfExtraDetailsSeries: "Series",
    tradeInAemPdfExtraDetailsChassis: "Chassis",
    tradeInAemPdfExtraDetailsModelYear: "Model year",
    tradeInAemPdfExtraDetailsEngineStartYear: "Engine start year",
    tradeInAemPdfExtraDetailsEngineEndYear: "Engine end year",
    tradeInAemPdfExtraDetailsTrimPackage: "Trim package",
    tradeInAemPdfExtraDetailsDriveline: "Driveline",

    // TGB
    tradeInAemPdfExtraDetailsType: "Type",

    // THEL
    tradeInAemPdfExtraDetailsName: "Name",
    tradeInAemPdfExtraDetailsCarID: "CarID",
    tradeInAemPdfExtraDetailsStart: "Start",
    tradeInAemPdfExtraDetailsEnd: "End",

    // TES
    tradeInAemPdfExraDetailsCategory: "Category",

    // ----------------------------------------------------------------------
    // Pdf labels (All pdf labels except "Extra vehicle details" as these have a separate section)
    // ----------------------------------------------------------------------

    // General
    tradeInAemPdfPageTitle: "Trade in valuation",
    tradeInAemPdfPageDescription: "Please find the estimated value of your old car.",
} as const;

export const tradeInAemNavStepLabels: Record<TradeInAemNavStep, TradeInAemLabelType> = {
    [TradeInAemNavStep.FindVehicle]: "tradeInAemStepFindVehicle",
    [TradeInAemNavStep.VehicleDetails]: "tradeInAemStepVehicleDetails",
    [TradeInAemNavStep.VehicleValue]: "tradeInAemStepVehicleValue",
    [TradeInAemNavStep.ModelSelect]: "tradeInAemStepModelSelect",
    [TradeInAemNavStep.DealerFinder]: "tradeInAemStepSelectDealer",
    [TradeInAemNavStep.ContactForm]: "tradeInAemStepContactForm",
    [TradeInAemNavStep.Summary]: "tradeInAemStepValuation",
};

export const tradeInAemHeaderTitleLabels: Record<TradeInAemNavStep, TradeInAemLabelType> = {
    [TradeInAemNavStep.FindVehicle]: "tradeInAemFindVehicleHeaderTitle",
    [TradeInAemNavStep.VehicleDetails]: "tradeInAemVehicleDetailsHeaderTitle",
    [TradeInAemNavStep.VehicleValue]: "tradeInAemVehicleValueHeaderTitle",
    [TradeInAemNavStep.ModelSelect]: "tradeInAemModelSelectHeaderTitle",
    [TradeInAemNavStep.DealerFinder]: "tradeInAemSelectDealerHeaderTitle",
    [TradeInAemNavStep.ContactForm]: "tradeInAemContactFormHeaderTitle",
    [TradeInAemNavStep.Summary]: "tradeInAemSummaryHeaderTitle",
};

export const tradeInAemHeaderDescriptionLabels: Record<TradeInAemNavStep, TradeInAemLabelType> = {
    [TradeInAemNavStep.FindVehicle]: "tradeInAemFindVehicleHeaderDescription",
    [TradeInAemNavStep.VehicleDetails]: "tradeInAemVehicleDetailsHeaderDescription",
    [TradeInAemNavStep.VehicleValue]: "tradeInAemVehicleValueHeaderDescription",
    [TradeInAemNavStep.ModelSelect]: "tradeInAemModelSelectHeaderDescription",
    [TradeInAemNavStep.DealerFinder]: "tradeInAemSelectDealerHeaderDescription",
    [TradeInAemNavStep.ContactForm]: "tradeInAemContactFormHeaderDescription",
    [TradeInAemNavStep.Summary]: "tradeInAemSummaryHeaderDescription",
};

export const tradeInAemModelSelectLabels: Record<TradeInAemModelSelectType, TradeInAemLabelType> = {
    [TradeInAemModelSelectType.New]: "tradeInAemModelSelectNewCars",
    [TradeInAemModelSelectType.Stock]: "tradeInAemModelSelectStockCars",
    [TradeInAemModelSelectType.Used]: "tradeInAemModelSelectUsedCars",
    [TradeInAemModelSelectType.None]: "tradeInAemModelSelectNone",
};

export const tradeInAemExtraVehicleDetailsPdfLabels: Record<
    TradeInAemExtraVehicleDetailsPdfAllPossibleKeysType,
    TradeInAemLabelType
> = {
    // Base
    manufacturer: "tradeInAemPdfExtraDetailsManufacturer",
    model: "tradeInAemPdfExtraDetailsModel",
    grade: "tradeInAemPdfExtraDetailsGrade",
    generation: "tradeInAemPdfExtraDetailsGeneration",
    engine: "tradeInAemPdfExtraDetailsEngine",
    powerHp: "tradeInAemPdfExtraDetailsPowerHp",
    powerPs: "tradeInAemPdfExtraDetailsPowerPs",
    powerKw: "tradeInAemPdfExtraDetailsPowerKw",
    body: "tradeInAemPdfExtraDetailsBody",
    doors: "tradeInAemPdfExtraDetailsDoors",
    fuel: "tradeInAemPdfExtraDetailsFuel",
    transmission: "tradeInAemPdfExtraDetailsTransmission",
    gears: "tradeInAemPdfExtraDetailsGears",
    colour: "tradeInAemPdfExtraDetailsColour",
    regDate: "tradeInAemPdfExtraDetailsRegDate",
    vin: "tradeInAemPdfExtraDetailsVin",
    seats: "tradeInAemPdfExtraDetailsSeats",
    wheeldrive: "tradeInAemPdfExtraDetailsWheeldrive",
    cc: "tradeInAemPdfExtraDetailsCc",
    year: "tradeInAemPdfExtraDetailsYear",

    // TAF
    engineSize: "tradeInAemPdfExtraDetailsEngineSize",
    numberOfGears: "tradeInAemPdfExtraDetailsNumberOfGears",
    priceNetto: "tradeInAemPdfExtraDetailsPriceNetto",
    priceBrutto: "tradeInAemPdfExtraDetailsPriceBrutto",
    trimLine: "tradeInAemPdfExtraDetailsTrimLine",
    driveType: "tradeInAemPdfExtraDetailsDriveType",

    // TMI

    // TSW
    series: "tradeInAemPdfExtraDetailsSeries",
    chassis: "tradeInAemPdfExtraDetailsChassis",
    modelYear: "tradeInAemPdfExtraDetailsModelYear",
    engineStartYear: "tradeInAemPdfExtraDetailsEngineStartYear",
    engineEndYear: "tradeInAemPdfExtraDetailsEngineEndYear",
    trimPackage: "tradeInAemPdfExtraDetailsTrimPackage",
    driveline: "tradeInAemPdfExtraDetailsDriveline",

    // TGB
    type: "tradeInAemPdfExtraDetailsType",

    // THEL
    name: "tradeInAemPdfExtraDetailsName",
    carID: "tradeInAemPdfExtraDetailsCarID",
    start: "tradeInAemPdfExtraDetailsStart",
    end: "tradeInAemPdfExtraDetailsEnd",

    // TES
    category: "tradeInAemPdfExraDetailsCategory",
};

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------
type TradeInAemLabelStateType = { commonSettings: CommonSettingsType; tradeInAemSettings: TradeInAemSettingsType };
export type TradeInAemLabelType = keyof typeof tradeInAemLabels;

type TradeInAemLabelParamType = Partial<
    Record<"line1" | "line2" | "city" | "zip" | "region" | "province" | "country" | "value", string | number>
>;

export const useTradeInAemLabel = getUseLabel(
    (state: TradeInAemLabelStateType) => state.tradeInAemSettings.labels,
    (state: TradeInAemLabelStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseLabelType<TradeInAemLabelType, TradeInAemLabelParamType>;

export const useTradeInAemOptionalLabel = getUseOptionalLabel(
    (state: TradeInAemLabelStateType) => state.tradeInAemSettings.labels,
    (state: TradeInAemLabelStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseOptionalLabelType<TradeInAemLabelType, TradeInAemLabelParamType>;

export const getTradeInAemLabel = getLabelFn(
    (state: TradeInAemLabelStateType) => state.tradeInAemSettings.labels,
    (state: TradeInAemLabelStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as GetLabelType<TradeInAemLabelStateType, TradeInAemLabelType, TradeInAemLabelParamType>;

export const TradeInAemLabel = getLabelComponent(useTradeInAemLabel) as LabelComponentType<
    TradeInAemLabelType,
    TradeInAemLabelParamType
>;
